import { Subscription } from 'rxjs/internal/Subscription';
import { PrivateClassTrackService } from '../../../home/live-class-revamp/private-class-banner/private-class-track/private-class-track.service';
import { BackboneAdapterService } from '../../../shared/backbone-adapter.service';
import { GTMService } from '../../../shared/gtm.service';
import { setLiveClassTags } from '../../../shared/helpers/one-signal-tags.helper';
import { NotificationService } from '../../../shared/notification.service';
import { OneSignalService } from '../../../shared/one-signal.service';
import { LiveClassDto } from '../../../shared/types/LiveClassDto';
import { LiveClassEventState } from '../../../shared/types/LiveClassEventState';
import { LiveClassEventType } from '../../../shared/types/LiveClassEventType';
import { AnalyticsOutcomeEvents } from '../../../shared/types/analytics-outcome.types';
import { ClassType } from '../../../shared/types/class-types.enum';
import { LIVE_CLASS_LOCKED_STATE_KEYS, PcRevampType } from '../../../shared/types/live-class-locked-state-keys';
import { NotificationDto } from '../../../shared/types/notification-dto';
import { OneSignalTags } from '../../../shared/types/one-signal-tags';
import { LiveClassEventDto } from '../../live-class-event-dto';
import { LiveClassEventService } from '../../live-class-event.service';
import { PrivateClassLesson } from '../../live-class-revamp/private-class-banner/private-class-lesson-dto';
import { LiveClassService } from '../../live-class.service';
import { ClassTile } from './class-tile';
import { UtilsService } from '../../../shared/utils.service';

declare var window: any;

interface HasSelectedLesson {
  selectedLesson?: { displayName?: string };
}

export class LiveClassTile extends ClassTile {
  protected liveClassEventSubscription: Subscription;
  private subscriptions = new Subscription();

  constructor(
    private liveClassService: LiveClassService,
    private liveClassEventService: LiveClassEventService,
    private notificationService: NotificationService,
    private backbone: BackboneAdapterService,
    private gtmService: GTMService,
    private oneSignalService: OneSignalService,
    private privateClassTrackService: PrivateClassTrackService,
    pcRevampType: PcRevampType,
    liveClass: LiveClassDto,
  ) {
    super(pcRevampType, liveClass);
    this.listenBackboneToLiveClassJoin();
    this.subscriptions.add(this.suscribeToNotificationService());
    this.suscribeToLiveClassEventService();
    this.initLiveClass();
  }

  private initLiveClass(): void {
    // This is how we can mock the state we want, we just need to call this.liveClassEventService.triggerEvent
    // 'WAITING_FOR_SIGNUP' | 'CAN_SIGN_UP' | 'SIGNUP_LESSON_SELECTION' | 'SIGNUP_CONFIRM' | 'SIGNED_UP' | 'HAS_JOIN' | 'HAS_REJOIN'
    // setTimeout(() => {
    //    if (this.liveClass.subType === LiveClassSubType.PRIVATE)
    //      this.liveClassEventService.triggerEvent('SIGNUP_LESSON_SELECTION', this.liveClass.id);
    // }, 500);

    switch (this.liveClass.liveClassStatus) {
      case 'SIGNED_UP':
        this.liveClassEventService.triggerEvent('SIGNED_UP', this.liveClass.id);
        break;
      case 'READY':
        this.liveClassEventService.triggerEvent('HAS_JOIN', this.liveClass.id);
        break;
      case 'JOINED':
        this.liveClassEventService.triggerEvent('HAS_REJOIN', this.liveClass.id);
        break;
      case 'COMPLETE':
        this.onComplete();
        break;
    }
  }

  public destroy() {
    if (this.liveClassEventSubscription) {
      this.liveClassEventSubscription.unsubscribe();
    }
    this.subscriptions.unsubscribe();
  }

  public onCancel(event: Event): void {
    this.gtmService.sendLiveClassEvent(LiveClassEventState.SIGNUP_CANCEL, this.liveClass);

    event.stopPropagation();
    this.liveClassEventService.triggerEvent('SIGNUP_CANCEL', null);
  }

  public sendGtmLiveClassEvent(state?: LiveClassEventState) {
    if (state) {
      this.gtmService.sendLiveClassEvent(state, this.liveClass);
    }
  }

  public onTileClick(lessonId?: number, lessonsWithFreeFormTopic?: PrivateClassLesson[]): void {
    switch (this.currentState) {
      case 'CAN_SIGN_UP':
        this.selectLesson(lessonsWithFreeFormTopic);
        break;
      case 'SIGNUP_LESSON_SELECTION':
        this.confirmSignUp();
        break;
      case 'SIGNUP_CONFIRM':
        this.signUp(lessonId);
        break;
      case 'HAS_JOIN':
        this.joinClass(this.liveClass.reminderId, this.liveClass.classroomUrl);
        break;
      case 'HAS_REJOIN':
        this.reJoinClass();
        break;
      case 'LOCKED':
        this.sendGtmLiveClassEvent(LiveClassEventState.LOCKED);
        break;
      case 'NONE':
        this.sendGtmLiveClassEvent(LiveClassEventState.NONE);
        break;
      default:
        this.sendGtmLiveClassEvent();
        break;
    }
  }

  private setOneSignalTagsForLiveClass(): void {
    const displayName = (this as HasSelectedLesson).selectedLesson && (this as HasSelectedLesson).selectedLesson.displayName;
    setLiveClassTags(this.liveClass, this.oneSignalService, displayName);

    if (this.liveClass.type === 'Private Class') {
      this.privateClassTrackService.getPrivateClassTrack().subscribe((track) => {
        this.oneSignalService.setTag(OneSignalTags.LAST_PRIVATE_CLASS_TAKEN_COLLECTION, track.name);
      });
    }
  }

  private listenBackboneToLiveClassJoin() {
    this.backbone.addListener('liveclass:join', (liveClassScheduleId) => {
      if (liveClassScheduleId === this.liveClass.id) {
        this.liveClassEventService.triggerEvent('HAS_REJOIN', this.liveClass.id);
        this.gtmService.sendLiveClassEvent(LiveClassEventState.JOINED, this.liveClass);
        this.oneSignalService.trackOutcome(
          this.liveClass.type === 'Private Class'
            ? AnalyticsOutcomeEvents.LAST_PC_ATTENDED
            : AnalyticsOutcomeEvents.LAST_GC_ATTENDED
        );
        this.setOneSignalTagsForLiveClass();
      }
    });
  }

  private suscribeToNotificationService() {
    return this.notificationService.onReceived.subscribe((notification: NotificationDto) => {
      if (notification.type === 'LiveClassNotification' && this.liveClass.id === notification.scheduleId) {
        this.liveClass.classroomUrl = notification.url;
        this.liveClass.reminderId = notification.id;
        this.liveClassEventService.triggerEvent('HAS_JOIN', notification.scheduleId);
        this.gtmService.sendLiveClassEvent(LiveClassEventState.CLASS_READY, this.liveClass, LiveClassEventType.SYSTEM);
      }
    });
  }

  private suscribeToLiveClassEventService() {
    this.liveClassEventSubscription = this.liveClassEventService.onChange.subscribe((event: LiveClassEventDto) => {
      if (!event) {
        return;
      }
      switch (event.event) {
        case 'CAN_SIGN_UP':
          if (this.currentState === 'NONE') {
            this.currentState = this.liveClass.signupEnabled ? 'CAN_SIGN_UP' : 'NONE';
          }
          break;
          case 'WAITING_FOR_FETCH_NEXT_LIVE_CLASSES':
            this.onWaitingForFetchNextLiveClassesEvent();
            break;
        case 'SIGNUP_LESSON_SELECTION':
          this.onLessonSelectEvent(event);
          break;
        case 'SIGNUP_CANCEL':
          this.currentState = this.liveClass.signupEnabled ? 'CAN_SIGN_UP' : 'NONE';
          break;
        case 'SIGNED_UP':
          this.onSignedUpEvent(event);
          break;
        case 'HAS_JOIN':
          this.onHasJoinEvent(event);
          break;
        case 'SIGNUP_CONFIRM':
          this.onSignupConfirmEvent(event);
          break;
        case 'HAS_REJOIN':
          this.onHasRejoinEvent(event);
          break;
      }
    });
  }

  protected onWaitingForFetchNextLiveClassesEvent(): void {
    this.setLockedState('studybundle.liveClasses.nextLiveClassWaitingMessage');
  }

  protected setLockedState(lockedTextKey: string): void {
    this.currentState = 'LOCKED';
    this.lockedTextKey = lockedTextKey;
  }

  // TO BE OVERWRITED IN group-class-tile && private-class-tile
  protected onComplete(): void {}

  // TO BE OVERWRITED IN group-class-tile && private-class-tile
  protected onLessonSelectEvent(event: LiveClassEventDto): void {}

  private onSignupConfirmEvent(event: LiveClassEventDto): void {
    if (this.liveClass && event.data === this.liveClass.id) {
      this.currentState = 'SIGNUP_CONFIRM';
    } else {
      this.setLockedState(LIVE_CLASS_LOCKED_STATE_KEYS.SUBSCRIBE[this.pcRevampType]);
    }
  }

  private onHasJoinEvent(event: LiveClassEventDto): void {
    if (this.liveClass && event.data === this.liveClass.id) {
      this.notificationService.stopLiveClassPolling();
      this.currentState = 'HAS_JOIN';
      this.signUpInProgress = false;
    } else {
      this.setLockedState(LIVE_CLASS_LOCKED_STATE_KEYS.REGISTER[this.pcRevampType]);
    }
  }

  private onHasRejoinEvent(event: LiveClassEventDto): void {
    if (this.liveClass && event.data === this.liveClass.id) {
      this.currentState = 'HAS_REJOIN';
      this.signUpInProgress = false;
    } else {
      this.setLockedState(LIVE_CLASS_LOCKED_STATE_KEYS.REGISTER[this.pcRevampType]);
    }
  }

  private onSignedUpEvent(event: LiveClassEventDto): void {
    if (this.liveClass && event.data === this.liveClass.id) {
      const ONE_MINUTE_IN_MS = 60 * 1000;
      this.notificationService.startLiveClassPolling(this.liveClass.classBegin + ONE_MINUTE_IN_MS);
      this.currentState = 'SIGNED_UP';
    } else {
      this.setLockedState(LIVE_CLASS_LOCKED_STATE_KEYS.REGISTER[this.pcRevampType]);
    }
  }

  // TO BE OVERWRITED IN group-class-tile && private-class-tile
  protected selectLesson(lessonsWithFreeFormTopic?: PrivateClassLesson[]) {
    this.liveClassEventService.triggerEvent('SIGNUP_LESSON_SELECTION', this.liveClass.id);
    this.gtmService.sendLiveClassEvent(LiveClassEventState.SIGNUP_LESSON_SELECTION, this.liveClass);
  }

  protected confirmSignUp(): void {
    this.liveClassEventService.triggerEvent('SIGNUP_CONFIRM', this.liveClass.id);
    this.gtmService.sendLiveClassEvent(LiveClassEventState.SIGNUP, this.liveClass);
    this.oneSignalService.trackOutcome(
      this.liveClass.type === 'Private Class'
        ? AnalyticsOutcomeEvents.LAST_PC_REQUESTED
        : AnalyticsOutcomeEvents.LAST_GC_REQUESTED
    );
  }

  private signUp(lessonId?: number): void {
    this.signUpInProgress = true;
    this.liveClassService.requestLiveClass(this.liveClass.id, lessonId).subscribe(
      () => this.liveClassEventService.triggerEvent('SIGNED_UP', this.liveClass.id),
      (err) => (this.signUpInProgress = false)
    );
    this.gtmService.sendLiveClassEvent(LiveClassEventState.SIGNUP_CONFIRMED, this.liveClass);
  }

  private joinClass(notificationId: number, classroomUrl: string): void {
    this.notificationService.setDeleteFlag(notificationId).subscribe(() => {
      this.liveClassEventService.triggerEvent('HAS_REJOIN', this.liveClass.id);
      window.open(this.getClassroomUrl(classroomUrl));
    });
    this.gtmService.sendLiveClassEvent(LiveClassEventState.JOINED, this.liveClass);
    this.oneSignalService.trackOutcome(
      this.liveClass.type === 'Private Class'
        ? AnalyticsOutcomeEvents.LAST_PC_ATTENDED
        : AnalyticsOutcomeEvents.LAST_GC_ATTENDED
    );
    this.setOneSignalTagsForLiveClass();
  }

  private reJoinClass() {
    window.open(this.getClassroomUrl(this.liveClass.classroomUrl));
    this.gtmService.sendLiveClassEvent(LiveClassEventState.REJOINED, this.liveClass);
  }

  private getClassroomUrl(classroomUrl: string): string {
    return classroomUrl;
  }
}



// WEBPACK FOOTER //
// ./ts/app/home/live-class-section/class-tile/live-class-tile.ts